import { useState, useEffect, useRef } from 'react'
import { sendChatMessage } from '../services/chatService'

function MCQOptions({ message, onSelectOption, isDisabled }) {
  const detectMCQ = (text) => {
    const cleanedText = text.replace(/\d{1,2}:\d{2}\s*(?:AM|PM)/gi, '').trim()
    const hasMultipleOptions = /[A-E]\.\s+.+/g
    const optionMatches = cleanedText.match(hasMultipleOptions)
    if (!optionMatches || optionMatches.length < 2) return null
    let firstOptionIndex = cleanedText.search(/\n[A-E]\.\s+/)
    if (firstOptionIndex === -1) return null
    const questionPart = cleanedText.substring(0, firstOptionIndex).trim()
    const optionsPart = cleanedText.substring(firstOptionIndex).trim()
    const optionPattern = /^([A-E])\.\s+(.+)$/
    const options = []
    for (const line of optionsPart.split('\n')) {
      let t = line.trim().replace(/\d{1,2}:\d{2}\s*(?:AM|PM).*$/gi, '').trim()
      const m = t.match(optionPattern)
      if (m) options.push({ letter: m[1], text: m[2].trim() })
    }
    return options.length >= 2 ? { question: questionPart, options } : null
  }

  const mcq = detectMCQ(message.text)
  if (!mcq) return <p className="mb-0 small" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{message.text}</p>

  return (
    <div>
      <p className="small fw-semibold mb-2" style={{ whiteSpace: 'pre-wrap' }}>{mcq.question}</p>
      <div className="d-flex flex-column gap-2 mt-2">
        {mcq.options.map(option => (
          <button
            key={option.letter}
            onClick={() => onSelectOption(option.letter)}
            disabled={isDisabled}
            className="btn btn-outline-primary text-start d-flex align-items-start gap-2 py-2"
            style={{ borderRadius: 8 }}
          >
            <span className="d-flex align-items-center justify-content-center rounded-circle bg-primary text-white fw-bold flex-shrink-0 small"
              style={{ width: 22, height: 22, fontSize: '0.7rem', marginTop: 1 }}>
              {option.letter}
            </span>
            <span className="small">{option.text}</span>
          </button>
        ))}
      </div>
      <p className="text-muted mt-2 mb-0" style={{ fontSize: '0.72rem' }}>💡 Click an option to select your answer</p>
    </div>
  )
}

export default function ChatBot() {
  const [messages, setMessages] = useState([{
    id: 1,
    text: "Hello! I'm Smart Doc Pro, your AI medical assistant. How can I help you today?",
    sender: 'bot',
    timestamp: new Date(),
  }])
  const [inputMessage, setInputMessage] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const messagesEndRef = useRef(null)
  const inputRef = useRef(null)

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }) }, [messages])
  useEffect(() => { inputRef.current?.focus() }, [])

  const sendMessage = async (messageText) => {
    setMessages(prev => [...prev, { id: Date.now(), text: messageText.trim(), sender: 'user', timestamp: new Date() }])
    setIsLoading(true); setError('')
    try {
      const response = await sendChatMessage(messageText.trim())
      setMessages(prev => [...prev, { id: Date.now() + 1, text: response.answer, sender: 'bot', timestamp: new Date() }])
    } catch (err) {
      setError(err.message || 'Failed to get response. Please try again.')
      setMessages(prev => [...prev, { id: Date.now() + 1, text: "I'm sorry, I'm having trouble connecting right now. Please try again in a moment.", sender: 'bot', timestamp: new Date(), isError: true }])
    } finally { setIsLoading(false) }
  }

  const handleSendMessage = async (e) => {
    e.preventDefault()
    if (!inputMessage.trim() || isLoading) return
    await sendMessage(inputMessage); setInputMessage('')
  }

  const handleOptionSelect = async (optionLetter) => { if (!isLoading) await sendMessage(optionLetter) }

  const handleClearChat = () => {
    if (confirm('Clear chat history?')) {
      setMessages([{ id: Date.now(), text: 'Chat cleared. How can I help you?', sender: 'bot', timestamp: new Date() }])
      setError('')
    }
  }

  return (
    <div className="d-flex flex-column h-100" style={{ background: '#f8f9fa' }}>
      {/* Header */}
      <div className="d-flex align-items-center justify-content-between px-3 py-3 text-white" style={{ background: '#2563eb', flexShrink: 0 }}>
        <div className="d-flex align-items-center gap-3">
          <div className="d-flex align-items-center justify-content-center rounded-circle bg-white flex-shrink-0" style={{ width: 40, height: 40 }}>
            <span style={{ fontSize: '1.4rem' }}>🤖</span>
          </div>
          <div>
            <p className="fw-semibold mb-0">Smart Doc Pro</p>
            <p className="mb-0 opacity-75" style={{ fontSize: '0.75rem' }}>AI Medical Assistant (OpenAI)</p>
          </div>
        </div>
        <button onClick={handleClearChat} className="btn btn-sm btn-light">Clear Chat</button>
      </div>

      {/* Error */}
      {error && (
        <div className="alert alert-danger rounded-0 mb-0 py-2 px-3 d-flex align-items-center gap-2" style={{ flexShrink: 0 }}>
          <i className="bi bi-exclamation-circle-fill small"></i>
          <span className="small">{error}</span>
        </div>
      )}

      {/* Messages */}
      <div className="flex-grow-1 overflow-y-auto px-3 py-3" style={{ overflowY: 'auto' }}>
        <div className="d-flex flex-column gap-3">
          {messages.map(message => (
            <div key={message.id} className={`d-flex ${message.sender === 'user' ? 'justify-content-end' : 'justify-content-start'}`}>
              <div style={{ maxWidth: '70%' }}>
                <div className={`rounded-3 px-3 py-2 ${
                  message.sender === 'user' ? 'bg-primary text-white'
                  : message.isError ? 'bg-danger-subtle text-danger'
                  : 'bg-white border'
                }`}>
                  {message.sender === 'bot' && !message.isError ? (
                    <MCQOptions message={message} onSelectOption={handleOptionSelect} isDisabled={isLoading} />
                  ) : (
                    <p className="mb-0 small" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{message.text}</p>
                  )}
                  <p className={`mb-0 mt-1 ${message.sender === 'user' ? 'text-white opacity-75' : 'text-muted'}`} style={{ fontSize: '0.68rem' }}>
                    {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
              </div>
            </div>
          ))}

          {isLoading && (
            <div className="d-flex justify-content-start">
              <div className="bg-white border rounded-3 px-3 py-2">
                <div className="d-flex align-items-center gap-2">
                  <div className="d-flex gap-1">
                    {[0, 150, 300].map(delay => (
                      <div key={delay} className="rounded-circle bg-secondary" style={{ width: 8, height: 8, animation: `bounce 0.6s ${delay}ms infinite alternate` }}></div>
                    ))}
                  </div>
                  <span className="small text-muted">Thinking...</span>
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input */}
      <div className="bg-white border-top px-3 py-3" style={{ flexShrink: 0 }}>
        <form onSubmit={handleSendMessage} className="d-flex gap-2">
          <input
            ref={inputRef}
            type="text"
            className="form-control"
            value={inputMessage}
            onChange={e => setInputMessage(e.target.value)}
            placeholder="Type your medical question here..."
            disabled={isLoading}
          />
          <button type="submit" disabled={isLoading || !inputMessage.trim()} className="btn btn-primary d-flex align-items-center gap-2 px-4">
            {isLoading ? (
              <><span className="spinner-border spinner-border-sm"></span><span>...</span></>
            ) : (
              <><i className="bi bi-send-fill"></i><span>Send</span></>
            )}
          </button>
        </form>
        <p className="text-muted mt-2 mb-0" style={{ fontSize: '0.72rem' }}>💡 Tip: Click MCQ options or type your own response</p>
      </div>

      <style>{`
        @keyframes bounce { from { transform: translateY(0) } to { transform: translateY(-6px) } }
      `}</style>
    </div>
  )
}
